import { Stack, Typography } from '@mui/material';
import type { ExposureOrGroup } from 'api/submission';
import { APPLICATION_STEPS, defaultBackendDateformat, STATES } from 'common/constants';
import { Modal } from 'components/Modal';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { QuestionEngine, useQuestionEngine } from 'components/lib/QuestionEngine';
import { formatDate } from 'helpers/utils';
import { isEmpty } from 'lodash-es';
import { forwardRef, useImperativeHandle, useState } from 'react';
import type { IDriver } from 'routes/Exposures/Drivers/Drives.types';
import * as yup from 'yup';
import type { DriverModalProps, DriverModalRef } from './DriverModal.types';

type TGroup = ExposureOrGroup<IDriver>;

const { fields } = APPLICATION_STEPS.drivers;

const DriverModal = forwardRef<DriverModalRef | undefined, DriverModalProps>(
  ({ handleAdd, handleUpdate, category }, ref) => {
    const [editingGroup, setEditingGroup] = useState<TGroup>({} as TGroup);
    const [open, setOpen] = useState(false);
    const isEditing = !isEmpty(editingGroup);
    const title = `${isEditing ? 'Update' : 'Add'} ${category}`;

    // Driver license validation based on state
    const getDriverLicenseValidation = () =>
      yup
        .string()
        .required()
        .when(['pol_drivers_license_state'], (deps, schema) => {
          const [pol_drivers_license_state] = deps;

          // Oregon: 9 numeric characters OR 1 alpha + 6 numeric characters OR 1 alpha + 7 numeric characters
          if (pol_drivers_license_state === STATES.OR) {
            return schema.test(
              'oregon-license',
              'Oregon driver license must be 9 numeric characters OR 1 alpha + 6-7 numeric characters',
              (value) => {
                if (!value) return false; // Required field

                // Pattern 1: 9 numeric characters
                if (/^\d{9}$/.test(value)) return true;

                // Pattern 2: 1 alpha + 6 numeric characters
                if (/^[A-Za-z]\d{6}$/.test(value)) return true;

                // Pattern 3: 1 alpha + 7 numeric characters
                if (/^[A-Za-z]\d{7}$/.test(value)) return true;

                return false;
              },
            );
          }

          // South Carolina: Numeric string 5-11 characters long
          if (pol_drivers_license_state === STATES.SC) {
            return schema.test(
              'south-carolina-license',
              'South Carolina driver license must be a numeric string 5-11 characters long',
              (value) => {
                if (!value) return false; // Required field

                // Must be numeric and between 5-11 characters
                return /^\d{5,11}$/.test(value);
              },
            );
          }

          return schema;
        });

    const customValidation = {
      pol_drivers_license_number: getDriverLicenseValidation(),
    };

    const { validateFormik, ...questionEngineProps } = useQuestionEngine<IDriver>({
      fields,
      customValidation,
    });

    const { formik, setState } = questionEngineProps;

    useImperativeHandle(ref, () => ({
      openModal: (group) => {
        if (group) {
          setEditingGroup(group);
          setState?.(group.data);
          formik?.setValues(group.data);
        }

        setOpen(true);
      },
    }));

    const resetModal = () => {
      setOpen(false);
      formik?.resetForm();
      setState?.({} as IDriver);
      setEditingGroup({} as TGroup);
    };

    const handleAction = async () => {
      const isValid = await validateFormik();

      if (!isValid) {
        return false;
      }

      const values: IDriver = {
        ...formik.values,
        pol_drivers_dob: formik.values.pol_drivers_dob
          ? formatDate(formik.values.pol_drivers_dob, defaultBackendDateformat)
          : '',
      };

      if (isEditing) {
        await handleUpdate(values, editingGroup?.id);
      } else {
        await handleAdd(values);
      }

      resetModal();
      return true;
    };

    return (
      <Modal
        open={open}
        title={title}
        onClose={resetModal}
        onCloseIconClick={() => resetModal()}
        modalContent={
          <Stack>
            <Typography variant="h2">Tell us about this driver</Typography>

            <QuestionEngine {...questionEngineProps} />
          </Stack>
        }
        modalFooter={
          <PrimaryButton text={title} fullWidth onClick={handleAction} data-test="add-driver" />
        }
      />
    );
  },
);

export default DriverModal;
